import React, { useCallback, useMemo } from 'react';
import cn from 'classnames';
import './UpgradeFlightClass.css';
import { useTheme } from '../../../../../theme';
import { useToggleable } from '../../../../../hooks';
import { useRenderers } from '../../../../../renderProps';
import CheckoutAdditionalService from '../../../../';
import { UpgradeFlightClass as CoreUpgradeFlightClass } from '../../../../../UpgradeFlightClass';
import { useDispatch, useSelector } from 'react-redux';
import {
	getIsUpgradeClassSelectedForAllSegments,
	getIsUpgradeClassServiceSelected,
	getIsUpgradeClassServiceSelectedBySegment,
	getOrder,
	getUpgradeClassServices
} from '../../../../../Checkout/store/order/selectors';
import {
	GdsServiceVipSubType,
	OrderAdditionalServiceGdsServiceServiceType,
	SaveOrderServicesMutationVariables,
	useSaveOrderServicesMutation
} from '@websky/graphql';
import { ServiceButton } from '../../../../types';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../../../../Checkout/components/Checkout/Stepbar/hooks';
import { fillOrder } from '../../../../../Checkout/store/order/actions';
import { useGdsServiceEventToggleable } from '../../../../../Checkout/components/Checkout/hooks';
import { ServiceSummary } from '../../../../../features/service-summary';
import ModalFullscreen from '../../../../../ModalFullscreen';
import { CheckoutState } from '../../../../../Checkout/store/reducers';
import { CardExtraViewVariant } from '@shared/ui/CardExtraView';

interface Props {
	mode?: CardExtraViewVariant;
	aggregationIcons?: React.ReactNode[];
	segmentId?: string;
}

const UpgradeFlightClassCheckout: React.FC<Props> = ({ mode, aggregationIcons, segmentId }) => {
	const { t } = useTranslation('Checkout');
	const service = useSelector(getUpgradeClassServices)?.[0];
	const order = useSelector(getOrder);
	const { onDeleteItem } = useCart();
	const dispatch = useDispatch();

	const isAggregatorMode = mode === 'aggregator';

	const isSelected = useSelector((state: CheckoutState) =>
		isAggregatorMode && segmentId
			? getIsUpgradeClassServiceSelectedBySegment(state, segmentId)
			: getIsUpgradeClassServiceSelected(state)
	);
	const isSelectedForAllSegments = useSelector(getIsUpgradeClassSelectedForAllSegments);

	const { UpgradeFlightCLassCheckoutService: css } = useTheme('CheckoutAdditionalService');
	const { isOpen, open, close } = useToggleable(false);

	if (!service) {
		return null;
	}

	useGdsServiceEventToggleable({
		services: [service],
		serviceTypes: [OrderAdditionalServiceGdsServiceServiceType.Vip],
		serviceSubTypes: [GdsServiceVipSubType.Upgrade],
		onOpen: open
	});

	const { CheckoutAdditionalService: CustomCheckoutAdditionalService, CheckoutUpgradeFlightClass } = useRenderers();
	const UpgradeFlightClass = CheckoutUpgradeFlightClass ?? CoreUpgradeFlightClass;
	const ServiceWrapper = CustomCheckoutAdditionalService?.CheckoutAdditionalService ?? CheckoutAdditionalService;

	const [saveOrderServices, { data, loading }] = useSaveOrderServicesMutation();

	const handleSaveServices = useCallback(
		async (params: SaveOrderServicesMutationVariables) => {
			const { data } = await saveOrderServices({
				variables: params
			});

			if (data.SaveOrderServices) {
				dispatch(fillOrder(data.SaveOrderServices));
			}

			close();
		},
		[data, loading, saveOrderServices]
	);

	const buttons: ServiceButton[] = useMemo(() => {
		if (isSelectedForAllSegments) {
			return null;
		}
		if (isSelected) {
			return [
				{
					action: open,
					text: t('Change'),
					buttonType: 'change',
					variant: 'text'
				}
			];
		} else {
			return [
				{
					action: open,
					text: t('Add'),
					buttonType: 'change',
					variant: 'optionEdit'
				}
			];
		}
	}, [isSelected, isSelectedForAllSegments]);

	return (
		<>
			<ServiceSummary
				serviceType="Upgrade"
				onDelete={onDeleteItem}
				segmentId={segmentId}
			>
				{children => (
					<ServiceWrapper
						mode={mode}
						buttons={buttons}
						header={service.name}
						className={cn(css.upgradeFlightClass, { [css.aggregatorUpgrade]: isAggregatorMode })}
						coverClassName={isAggregatorMode ? css.aggregatorCover : undefined}
						description={service.description}
						isSelected={isSelected}
						addClick={open}
						priceFrom={service.price}
						priceTotal={order.price.vip}
						aggregationIcons={aggregationIcons}
						hideFooterButtons={isAggregatorMode && isSelected}
					>
						{children}
					</ServiceWrapper>
				)}
			</ServiceSummary>

			<ModalFullscreen
				isOpen={isOpen}
				onClose={close}
				classes={{
					paper: css.paper,
					top_wrapper: css.top_wrapper,
					content: css.content
				}}
				ignoreClose={false}
				keepMounted={false}
			>
				<UpgradeFlightClass
					mode={mode}
					onBackClick={close}
					onSaveServices={handleSaveServices}
					loading={loading}
				/>
			</ModalFullscreen>
		</>
	);
};

export default UpgradeFlightClassCheckout;
